import { useState, useCallback, useMemo, useEffect, useRef } from 'react'

import { useFetch } from '@core/hooks'

import { Box, Button, Text } from '@components/atoms'
import { Icon } from '@components/molecules'
import {
  StyledFileUploadField,
  FieldLabel,
  ErrorMessage,
  HelperMessage
} from './FileUploadFieldAlt.styled'
import { useTranslation } from 'react-i18next'

import axios from 'axios'
import { v4 as uuid } from 'uuid'
import * as mime from 'mime-types'
import { noop, some, get } from 'lodash'
import { getFileExtension, callToast, removeFileExtension } from '@core/utils'
import { UPLOAD_POLICIES } from '@constants/files'
import { APIS } from '@constants/apis'
import { downloadFileFromUrl, downloadURI } from '@core/utils/download'
import { buildCompanyStorageRequest } from '@core/utils/companyStorage'

const FileUploadFieldAlt = ({
  name,
  label,
  error,
  helperMessage,
  value,
  touched,
  className,
  optionalPayload,
  storageCompanyId,
  storageAssetType,
  generateUniqueFileName = false,
  legacyDownloadFallback = false,
  acceptList = [],
  uploadKey = 'key',
  policies = UPLOAD_POLICIES.FILE,
  setFieldValue = noop,
  setFieldError = noop,
  setFile = noop,
  required = false
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [displayValue, setDisplayValue] = useState(null)
  const localFile = useRef(null)
  const latestUpload = useRef(0)
  const uploadBusy = useRef(false)
  const context = useRef(storageCompanyId)
  context.current = storageCompanyId

  const { execute: getPresignedUploadUrl } = useFetch(
    storageAssetType
      ? APIS.FILES.GET_COMPANY_STORAGE_UPLOAD_URL
      : APIS.FILES.GET_PRESIGNED_UPLOAD_URL
  )

  const { execute: _getPresignedDownloadUrl } = useFetch(
    storageAssetType
      ? APIS.FILES.GET_COMPANY_STORAGE_DOWNLOAD_URL
      : APIS.FILES.GET_PRESIGNED_DOWNLOAD_URL
  )

  const { execute: getLegacyDownloadUrl } = useFetch(
    APIS.FILES.GET_PRESIGNED_DOWNLOAD_URL
  )

  const getDownloadPresign = useCallback(
    (request) => {
      _getPresignedDownloadUrl(request, {
        onSuccess: async (res) => {
          if (context.current !== storageCompanyId) return
          const { url, key: legacyKey, objectKey } = get(res, 'data')
          const key = storageAssetType ? objectKey : legacyKey
          if (url && key) {
            try {
              await downloadFileFromUrl(url, key)
            } catch (downloadError) {
              if (context.current !== storageCompanyId) return
              if (
                storageAssetType &&
                legacyDownloadFallback &&
                downloadError.response?.status === 404
              ) {
                return getLegacyDownloadUrl(
                  {
                    payload: {
                      ...optionalPayload,
                      key: value,
                      fileExtension: '.' + getFileExtension({ name: value }),
                      [uploadKey]: removeFileExtension(value)
                    }
                  },
                  {
                    onSuccess: async ({ data }) => {
                      if (context.current !== storageCompanyId) return
                      if (!data?.url || !data?.key)
                        throw new Error('Invalid download response')
                      await downloadFileFromUrl(data.url, data.key)
                      if (context.current !== storageCompanyId) return
                      callToast({
                        type: 'success',
                        message: t('common.toast.get.success')
                      })
                    },
                    onFailed: () => {
                      if (context.current !== storageCompanyId) return
                      callToast({
                        type: 'error',
                        message: t('common.toast.get.failed')
                      })
                    }
                  }
                )
              }
              throw downloadError
            }
            if (context.current !== storageCompanyId) return
            callToast({
              type: 'success',
              message: t('common.toast.get.success')
            })
          } else {
            throw new Error('Invalid download response')
          }
        },
        onFailed: () => {
          if (context.current !== storageCompanyId) return
          callToast({
            type: 'error',
            message: t('common.toast.get.failed')
          })
        }
      })
    },
    [
      _getPresignedDownloadUrl,
      getLegacyDownloadUrl,
      legacyDownloadFallback,
      optionalPayload,
      storageAssetType,
      storageCompanyId,
      t,
      uploadKey,
      value
    ]
  )

  const errorMessageWidget = useMemo(() => {
    return (
      error &&
      touched && (
        <ErrorMessage className={`error-text ${error ? 'input-error' : ''}`}>
          {error}
        </ErrorMessage>
      )
    )
  }, [error, touched])

  const helperMessageWidget = useMemo(() => {
    return (
      helperMessage && (
        <HelperMessage className="helper-text">{helperMessage}</HelperMessage>
      )
    )
  }, [helperMessage])

  const inputRef = useRef(null)

  const downloadRef = useCallback(() => {
    const file = storageAssetType
      ? localFile.current?.key === value
        ? localFile.current.file
        : null
      : inputRef.current?.fileList?.[0]?.originFileObj
    if (file) {
      return downloadURI(file)
    }
    if (value) {
      if (storageAssetType) {
        try {
          return getDownloadPresign(
            buildCompanyStorageRequest(
              storageCompanyId,
              storageAssetType,
              value
            )
          )
        } catch {
          callToast({ type: 'error', message: t('common.toast.get.failed') })
          return
        }
      }
      const payload = {
        key: value,
        fileExtension: '.' + getFileExtension({ name: value }),
        [uploadKey]: removeFileExtension(value)
      }
      return getDownloadPresign({ payload })
    }
  }, [
    getDownloadPresign,
    storageAssetType,
    storageCompanyId,
    t,
    uploadKey,
    value
  ])

  const displayValueWidget = useMemo(() => {
    return (
      displayValue && (
        <Box display="flex" alignItems="center">
          <a onClick={downloadRef}>
            <Text ml={10} mr="6px" color="#707D92" fontSize={14}>
              {displayValue}
            </Text>
          </a>
          <Icon
            icon="Bin"
            size="16"
            color="#F47373"
            disabled={generateUniqueFileName && isLoading}
            onClick={() => {
              if (generateUniqueFileName && uploadBusy.current) return
              localFile.current = null
              setFile(null)
              setFieldValue(name, null)
            }}
          />
        </Box>
      )
    )
  }, [
    displayValue,
    downloadRef,
    generateUniqueFileName,
    isLoading,
    name,
    setFieldValue,
    setFile
  ])

  useEffect(() => {
    setDisplayValue(
      generateUniqueFileName &&
        localFile.current &&
        localFile.current.key === value
        ? localFile.current.file.name
        : value
    )
    if (localFile.current?.key !== value) localFile.current = null
  }, [generateUniqueFileName, value])

  useEffect(() => {
    latestUpload.current += 1
    localFile.current = null
    uploadBusy.current = false
    setIsLoading(false)
    return () => {
      latestUpload.current += 1
    }
  }, [storageCompanyId])

  const typeBeforeUpload = useCallback(
    (file) => {
      if (generateUniqueFileName && uploadBusy.current) return false
      const extension = getFileExtension(file)
      const type = generateUniqueFileName ? extension.toLowerCase() : extension

      if (acceptList.length === 0) {
        return true
      }

      const inAcceptList = some(acceptList, (values) => values === type)
      if (inAcceptList) {
        return true
      }

      callToast({
        type: 'error',
        message: t('error.default.1007')
      })
      return false
    },
    [acceptList, generateUniqueFileName, t]
  )

  const uploadHandler = useCallback(
    ({ file }) => {
      if (generateUniqueFileName && uploadBusy.current) return
      if (file && file.size <= policies.SIZE) {
        const uploadId = ++latestUpload.current
        const isCurrent = () =>
          uploadId === latestUpload.current &&
          context.current === storageCompanyId
        uploadBusy.current = true
        setIsLoading(true)
        const fileName = generateUniqueFileName
          ? `${uuid()}.${getFileExtension(file).toLowerCase()}`
          : file.name
        const payload = {
          key: fileName,
          fileExtension: '.' + getFileExtension({ name: fileName }),
          [uploadKey]: removeFileExtension(fileName),
          ...optionalPayload
        }
        let request = { payload }
        try {
          if (storageAssetType) {
            request = buildCompanyStorageRequest(
              storageCompanyId,
              storageAssetType,
              fileName,
              'upload'
            )
          }
        } catch {
          uploadBusy.current = false
          setIsLoading(false)
          callToast({ type: 'error', message: t('error.default.file-upload') })
          return
        }

        const callback = {
          onSuccess: async ({ data }) => {
            if (!isCurrent()) return
            try {
              const key = storageAssetType
                ? data?.objectKey?.split('/').pop()
                : data?.key
              if (!name || !key || !data.url)
                throw new Error('Invalid upload response')
              await axios.put(data.url, file, {
                headers: {
                  'Content-Type': mime.lookup(file.name)
                }
              })

              if (isCurrent()) {
                localFile.current = { key, file }
                setFile(file)
                setFieldError(name, undefined)
                setFieldValue(name, key)
                setDisplayValue(file.name)
              }
            } catch {
              if (isCurrent())
                callToast({
                  type: 'error',
                  message: t('error.default.file-upload')
                })
            }
          },
          onFailed: () => {
            if (isCurrent())
              callToast({
                type: 'error',
                message: t('error.default.file-upload')
              })
          },
          onFinally: () => {
            if (isCurrent()) {
              uploadBusy.current = false
              setIsLoading(false)
            }
          }
        }

        getPresignedUploadUrl(request, callback)
      } else {
        callToast({
          type: 'error',
          message: 'Exceed limit'
        })
      }
    },
    [
      generateUniqueFileName,
      getPresignedUploadUrl,
      name,
      optionalPayload,
      policies.SIZE,
      setFieldError,
      setFieldValue,
      setFile,
      storageAssetType,
      storageCompanyId,
      t,
      uploadKey
    ]
  )

  return (
    <Box className={className} display="flex" flexDirection="column">
      {label && (
        <FieldLabel htmlFor={name}>
          <span>{label}</span>
          {required && <span className="star">*</span>}
        </FieldLabel>
      )}
      <Box display="flex" alignItems="center">
        <StyledFileUploadField
          ref={inputRef}
          showUploadList={false}
          beforeUpload={typeBeforeUpload}
          customRequest={uploadHandler}
          maxCount={1}
          disabled={generateUniqueFileName && isLoading}
          accept={policies.MIME_TYPE}
        >
          <Button
            loading={isLoading}
            disabled={generateUniqueFileName && isLoading}
          >
            {!isLoading && <Icon icon="Upload" color="#fff" />}
            Upload
          </Button>
        </StyledFileUploadField>
        {displayValueWidget}
      </Box>
      {errorMessageWidget}
      {helperMessageWidget}
    </Box>
  )
}

export default FileUploadFieldAlt
